// @ts-ignore
import * as ebml from 'ts-ebml/dist/EBML.min.js';
import { v4 as uuidV4 } from 'uuid';
import { getRoomName } from '../../../base/conference/functions';
import { isMobileBrowser } from '../../../base/environment/utils';
import { browser } from '../../../base/lib-jitsi-meet';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { getLocalTrack, getTrackState } from '../../../base/tracks/functions';
import { isEmbedded } from '../../../base/util/embedUtils';
import { stopLocalVideoRecording } from '../../actions.any';
import logger from '../../logger';
/** iframe ↔ Electron 宿主 postMessage 类型（与 Electron 端常量保持一致）。 */
const ELECTRON_RECORDING_MESSAGE_TYPE = 'jitsi-electron-recording';
/** 默认视频码率（bps）。 */
const DEFAULT_VIDEO_BIT_RATE = 2500000;
/** MediaRecorder timeslice（毫秒）。 */
const RECORDER_TIMESLICE_MS = 5000;
/** 首块时长占位（毫秒，24h）。 */
const DURATION_PLACEHOLDER_MS = 864000000;
/** 桥接请求超时（毫秒）。 */
const ELECTRON_BRIDGE_TIMEOUT_MS = 60000;
/**
 * After a lot of trial and error, this is the preferred media type for
 * local recording. It is the only one that works across all platforms, with the
 * only caveat being that the resulting file wouldn't be seekable.
 *
 * We solve that by fixing the first Blob in order to reserve the space for the
 * corrected metadata, and after the recording is done, we do it again, this time with
 * the real duration, and overwrite the first part of the file.
 */
const PREFERRED_MEDIA_TYPE = 'video/webm;codecs=vp8,opus';
/**
 * 向 Electron 父窗口发起桥接请求并等待响应。
 *
 * @param {string} action - 动作名。
 * @param {Record<string, any>} payload - 载荷。
 * @returns {Promise<any>}
 */
function requestElectronRecordingBridge(action, payload = {}) {
    return new Promise((resolve, reject) => {
        const requestId = uuidV4();
        let settled = false;
        const cleanup = () => {
            window.removeEventListener('message', onMessage);
            clearTimeout(timer);
        };
        const onMessage = (event) => {
            const data = event.data;
            if (!data
                || data.type !== ELECTRON_RECORDING_MESSAGE_TYPE
                || data.action !== 'response'
                || data.requestId !== requestId) {
                return;
            }
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            if (data.error) {
                reject(new Error(String(data.error)));
            }
            else {
                resolve(data);
            }
        };
        const timer = setTimeout(() => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            reject(new Error(`Electron recording bridge timeout: ${action}`));
        }, ELECTRON_BRIDGE_TIMEOUT_MS);
        window.addEventListener('message', onMessage);
        window.parent.postMessage({
            type: ELECTRON_RECORDING_MESSAGE_TYPE,
            action,
            requestId,
            ...payload
        }, '*');
    });
}
/**
 * 创建 Electron 写流适配器。
 *
 * @param {string} sessionId - 主进程会话 ID。
 * @returns {IElectronWritableStream}
 */
function createElectronWritableStream(sessionId) {
    let seekPosition = 0;
    return {
        async write(data) {
            const chunk = await data.arrayBuffer();
            await requestElectronRecordingBridge('write', {
                chunk,
                sessionId
            });
            seekPosition += chunk.byteLength;
        },
        async seek(position) {
            seekPosition = position;
        },
        async close() {
            await requestElectronRecordingBridge('end', { sessionId });
        },
        // Used only for seek+write of first chunk metadata.
        async writeAtCurrentSeek(data) {
            const chunk = await data.arrayBuffer();
            await requestElectronRecordingBridge('seek-write', {
                chunk,
                position: seekPosition,
                sessionId
            });
        }
    };
}
const LocalRecordingManager = {
    recorder: undefined,
    stream: undefined,
    audioContext: undefined,
    audioDestination: undefined,
    roomName: '',
    selfRecording: {
        on: false,
        withVideo: false
    },
    firstChunk: undefined,
    fileHandle: undefined,
    startTime: undefined,
    writableStream: undefined,
    electronSessionId: undefined,
    electronWritable: undefined,
    get mediaType() {
        if (this.selfRecording.on && !this.selfRecording.withVideo) {
            return 'audio/webm;';
        }
        return PREFERRED_MEDIA_TYPE;
    },
    /**
     * Initializes audio context used for mixing audio tracks.
     *
     * @returns {void}
     */
    initializeAudioMixer() {
        this.audioContext = new AudioContext();
        this.audioDestination = this.audioContext.createMediaStreamDestination();
    },
    /**
     * Mixes multiple audio tracks to the destination media stream.
     *
     * @param {MediaStream} stream - The stream to mix.
     * @returns {void}
     * */
    mixAudioStream(stream) {
        if (stream.getAudioTracks().length > 0 && this.audioDestination) {
            this.audioContext?.createMediaStreamSource(stream).connect(this.audioDestination);
        }
    },
    /**
     * Adds audio track to the recording stream.
     *
     * @param {MediaStreamTrack} track - The track to be added.
     * @returns {void}
     */
    addAudioTrackToLocalRecording(track) {
        if (this.selfRecording.on) {
            return;
        }
        if (track) {
            const stream = new MediaStream([track]);
            this.mixAudioStream(stream);
        }
    },
    /**
     * Returns a filename based ono the Jitsi room name in the URL and timestamp.
     *
     * @returns {string}
     * */
    getFilename() {
        const now = new Date();
        const timestamp = now.toISOString();
        return `${this.roomName}_${timestamp}`;
    },
    /**
     * Stops local recording.
     *
     * @returns {void}
     * */
    stopLocalRecording() {
        this.recorder?.stop();
    },
    /**
     * Starts a local recording.
     *
     * @param {IStore} store - The redux store.
     * @param {boolean} onlySelf - Whether to record only self streams.
     * @returns {void}
     */
    async startLocalRecording(store, onlySelf) {
        const { dispatch, getState } = store;
        const config = getState()['features/base/config'];
        const videoBitsPerSecond = config.localRecording?.videoBitsPerSecond
            || DEFAULT_VIDEO_BIT_RATE;
        const useElectronBridge = browser.isElectron();
        this.roomName = getRoomName(getState()) ?? '';
        if (useElectronBridge) {
            const startResult = await requestElectronRecordingBridge('start', {
                suggestedName: `${this.getFilename()}.webm`
            });
            this.electronSessionId = startResult.sessionId;
            this.electronWritable = createElectronWritableStream(startResult.sessionId);
            logger.info(`Local recording started via Electron bridge, session=${startResult.sessionId}`);
        }
        else {
            // Get a handle to the file we are going to write.
            const options = {
                startIn: 'downloads',
                suggestedName: `${this.getFilename()}.webm`
            };
            // @ts-expect-error
            this.fileHandle = await window.showSaveFilePicker(options);
            this.writableStream = await this.fileHandle?.createWritable();
        }
        const supportsCaptureHandle = !isEmbedded() && !browser.isElectron();
        const tabId = uuidV4();
        this.selfRecording.on = onlySelf;
        let gdmStream = new MediaStream();
        const tracks = getTrackState(getState());
        if (onlySelf) {
            const audioTrack = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)?.jitsiTrack?.track;
            let videoTrack = getLocalTrack(tracks, MEDIA_TYPE.VIDEO)?.jitsiTrack?.track;
            if (videoTrack && videoTrack.readyState !== 'live') {
                videoTrack = undefined;
            }
            if (!audioTrack && !videoTrack) {
                throw new Error('NoLocalStreams');
            }
            this.selfRecording.withVideo = Boolean(videoTrack);
            const localTracks = [];
            audioTrack && localTracks.push(audioTrack.clone());
            videoTrack && localTracks.push(videoTrack.clone());
            this.stream = new MediaStream(localTracks);
        }
        else {
            if (supportsCaptureHandle) {
                // @ts-ignore
                navigator.mediaDevices.setCaptureHandleConfig({
                    handle: `JitsiMeet-${tabId}`,
                    permittedOrigins: ['*']
                });
            }
            gdmStream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    displaySurface: 'browser',
                    frameRate: 30
                },
                audio: {
                    autoGainControl: false,
                    channelCount: 2,
                    echoCancellation: false,
                    noiseSuppression: false,
                    // @ts-ignore
                    restrictOwnAudio: false,
                    // @ts-ignore
                    suppressLocalAudioPlayback: false
                },
                // @ts-ignore
                preferCurrentTab: true,
                surfaceSwitching: 'exclude'
            });
            const gdmVideoTrack = gdmStream.getVideoTracks()[0];
            if (supportsCaptureHandle) {
                const isBrowser = gdmVideoTrack.getSettings().displaySurface === 'browser';
                const matchesHandle = (supportsCaptureHandle // @ts-ignore
                    && gdmVideoTrack.getCaptureHandle()?.handle === `JitsiMeet-${tabId}`);
                if (!isBrowser || !matchesHandle) {
                    gdmStream.getTracks().forEach((track) => track.stop());
                    throw new Error('WrongSurfaceSelected');
                }
            }
            this.initializeAudioMixer();
            const gdmAudioTrack = gdmStream.getAudioTracks()[0];
            if (!gdmAudioTrack) {
                // Electron / some capture paths may omit tab audio; still allow mic-only mix.
                logger.warn('No getDisplayMedia audio track; continuing with local mic only');
            }
            else {
                this.addAudioTrackToLocalRecording(gdmAudioTrack);
            }
            const localAudioTrack = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)?.jitsiTrack?.track;
            if (localAudioTrack) {
                this.addAudioTrackToLocalRecording(localAudioTrack);
            }
            let mixedAudioTracks = this.audioDestination?.stream.getAudioTracks() || [];
            // Electron 常拿不到标签页音频，且用户可能无麦：注入静音轨以便仍可录制屏幕画面
            if (!mixedAudioTracks.length && gdmVideoTrack && this.audioContext && this.audioDestination) {
                logger.warn('No mic/tab audio for local recording; injecting silent audio track');
                const oscillator = this.audioContext.createOscillator();
                const gain = this.audioContext.createGain();
                gain.gain.value = 0;
                oscillator.connect(gain);
                gain.connect(this.audioDestination);
                oscillator.start();
                mixedAudioTracks = this.audioDestination.stream.getAudioTracks() || [];
            }
            if (!mixedAudioTracks.length) {
                gdmStream.getTracks().forEach((track) => track.stop());
                throw new Error('NoAudioTrackFound');
            }
            this.stream = new MediaStream([
                ...mixedAudioTracks,
                gdmVideoTrack
            ]);
        }
        this.recorder = new MediaRecorder(this.stream, {
            // @ts-ignore
            audioBitrateMode: 'constant',
            mimeType: this.mediaType,
            videoBitsPerSecond: videoBitsPerSecond
        });
        // Serialize all chunk writes so the stop handler can await them before closing the
        // stream. Without this, the stop event fires concurrently with the last dataavailable
        // callback's async fixDuration call, and close() races the write — leaving an empty
        // file for recordings shorter than the 5-second timeslice.
        let writeQueue = Promise.resolve();
        this.recorder.addEventListener('dataavailable', e => {
            if (e.data && e.data.size > 0) {
                const chunk = e.data;
                writeQueue = writeQueue.then(async () => {
                    let data = chunk;
                    if (!this.firstChunk) {
                        this.firstChunk = data = await fixDuration(chunk, DURATION_PLACEHOLDER_MS);
                    }
                    if (this.electronWritable) {
                        await this.electronWritable.write(data);
                    }
                    else {
                        await this.writableStream?.write(data);
                    }
                });
            }
        });
        this.recorder.addEventListener('start', () => {
            this.startTime = Date.now();
        });
        this.recorder.addEventListener('stop', async () => {
            const duration = Date.now() - this.startTime;
            this.stream?.getTracks().forEach((track) => track.stop());
            gdmStream?.getTracks().forEach((track) => track.stop());
            this.recorder = undefined;
            this.audioContext?.close();
            this.audioContext = undefined;
            this.audioDestination = undefined;
            this.startTime = undefined;
            this.stream = undefined;
            this.selfRecording.on = false;
            this.selfRecording.withVideo = false;
            // Wait for all pending dataavailable writes before closing the stream.
            await writeQueue;
            try {
                if (this.firstChunk) {
                    const fixed = await fixDuration(this.firstChunk, duration);
                    if (this.electronWritable) {
                        await this.electronWritable.seek(0);
                        // Prefer seek-write helper if present on adapter.
                        const electronStream = this.electronWritable;
                        if (electronStream.writeAtCurrentSeek) {
                            await electronStream.writeAtCurrentSeek(fixed);
                        }
                        else {
                            await this.electronWritable.write(fixed);
                        }
                        await this.electronWritable.close();
                    }
                    else if (this.writableStream) {
                        await this.writableStream.seek(0);
                        await this.writableStream.write(fixed);
                        await this.writableStream.close();
                    }
                }
                else if (this.electronWritable) {
                    await this.electronWritable.close();
                }
                else if (this.writableStream) {
                    await this.writableStream.close();
                }
            }
            catch (e) {
                logger.error('Error while writing to the local recording file', e);
            }
            finally {
                this.firstChunk = undefined;
                this.fileHandle = undefined;
                this.writableStream = undefined;
                this.electronWritable = undefined;
                this.electronSessionId = undefined;
            }
        });
        if (!onlySelf) {
            gdmStream?.addEventListener('inactive', () => {
                dispatch(stopLocalVideoRecording());
            });
            this.stream.addEventListener('inactive', () => {
                dispatch(stopLocalVideoRecording());
            });
        }
        this.recorder.start(RECORDER_TIMESLICE_MS);
    },
    /**
     * Whether or not local recording is supported.
     *
     * @returns {boolean}
     */
    isSupported() {
        if (!MediaRecorder.isTypeSupported(PREFERRED_MEDIA_TYPE)) {
            return false;
        }
        if (browser.isElectron()) {
            // Electron: require parent bridge (iframe) and config flag is checked at start.
            try {
                return typeof window.parent !== 'undefined' && window.parent !== window.self;
            }
            catch {
                return true;
            }
        }
        return browser.isChromiumBased()
            && !browser.isReactNative()
            && !isMobileBrowser()
            // @ts-expect-error
            && Boolean(navigator.mediaDevices.setCaptureHandleConfig)
            // @ts-expect-error
            && typeof window.showSaveFilePicker !== 'undefined';
    },
    /**
     * Whether or not we're currently recording locally.
     *
     * @returns {boolean}
     */
    isRecordingLocally() {
        return Boolean(this.recorder);
    }
};
/**
 * Fixes the duration in the WebM container metadata.
 * Note: cues are omitted.
 *
 * @param {Blob} data - The first Blob of WebM data.
 * @param {number} duration - Actual duration of the video in milliseconds.
 * @returns {Promise<Blob>}
 */
async function fixDuration(data, duration) {
    const decoder = new ebml.Decoder();
    const reader = new ebml.Reader();
    reader.logging = false;
    reader.drop_default_duration = false;
    const dataBuf = await data.arrayBuffer();
    const elms = decoder.decode(dataBuf);
    for (const elm of elms) {
        reader.read(elm);
    }
    reader.stop();
    const newMetadataBuf = ebml.tools.makeMetadataSeekable(reader.metadatas, duration, [] // No cues
    );
    const body = new Uint8Array(dataBuf).subarray(reader.metadataSize);
    // @ts-ignore
    return new Blob([newMetadataBuf, body], { type: data.type });
}
export default LocalRecordingManager;
